import type { LucideIcon } from 'lucide-react'
import { motion } from 'framer-motion'
import type { ReactNode } from 'react'
import { scoreStroke } from '../../lib/utils'
import { BRAND } from '../../config'
import { cn } from '../../lib/utils'

// ─── Score chip (category pts) ────────────────────────────────────────────────

export function ScoreChip({ score, max }: { score: number; max: number }) {
  const stroke = scoreStroke(score, max)
  const pct = Math.min(Math.max(score / max, 0), 1)
  const circumference = 62.83
  const showPts = max !== 100

  return (
    <div className="flex shrink-0 items-center gap-2.5 rounded-[14px] bg-[#f2f2f6] px-4 py-2">
      <p className="text-[24px] font-bold tabular-nums leading-none" style={{ color: stroke }}>
        {score}
        <span className="text-[22px] font-medium text-[#99a1af]">/{max}</span>
        {showPts && <span className="ms-1 text-[11px] font-medium text-[#99a1af]">pts</span>}
      </p>
      <svg width={22} height={22} viewBox="0 0 32 32" aria-hidden>
        <circle cx={16} cy={16} r={11} fill="none" stroke="#e5e7eb" strokeWidth={3.5} />
        <circle
          cx={16}
          cy={16}
          r={11}
          fill="none"
          stroke={stroke}
          strokeWidth={3.5}
          strokeLinecap="round"
          strokeDasharray={circumference}
          strokeDashoffset={circumference * (1 - pct)}
          transform="rotate(-90 16 16)"
        />
      </svg>
    </div>
  )
}

// ─── Header meta chip (ranked in X of Y, photo counts, etc.) ──────────────────

export function HeaderMetaChip({ children }: { children: ReactNode }) {
  return (
    <div className="flex shrink-0 items-center rounded-[14px] bg-[#f2f2f6] px-4 py-2.5 text-xs leading-snug text-[#6a7282]">
      {children}
    </div>
  )
}

// ─── Section card shell ───────────────────────────────────────────────────────

export function ReportSectionCard({
  children,
  delay = 0.2,
  id,
  className,
  variant = 'default',
  'data-testid': testId,
}: {
  children: ReactNode
  delay?: number
  id?: string
  className?: string
  variant?: 'default' | 'nested'
  'data-testid'?: string
}) {
  return (
    <motion.section
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay }}
      id={id}
      data-testid={testId}
      className={cn(
        'overflow-hidden rounded-2xl',
        variant === 'default' ? 'mb-8 bg-white' : 'bg-[#f2f2f6]',
        className,
      )}
    >
      {children}
    </motion.section>
  )
}

// ─── Unified section header ───────────────────────────────────────────────────

export interface ReportSectionHeaderProps {
  icon?: LucideIcon
  iconNode?: ReactNode
  accentColor?: string
  badge?: number | string
  title: string
  subtitle?: string
  titleId?: string
  titleExtra?: ReactNode
  right?: ReactNode
  bordered?: boolean
}

export function ReportSectionHeader({
  icon: Icon,
  iconNode,
  accentColor = '#99a1af',
  badge,
  title,
  subtitle,
  titleId,
  titleExtra,
  right,
  bordered = true,
}: ReportSectionHeaderProps) {
  return (
    <div
      className={cn(
        'flex flex-wrap items-start justify-between gap-4 px-4 py-5 sm:px-6',
        bordered && 'border-b border-[#f3f4f6]',
      )}
    >
      <div className="flex min-w-0 items-start gap-3">
        {(Icon || iconNode) && (
          <div
            className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full"
            style={{ backgroundColor: `${accentColor}18` }}
          >
            {iconNode ?? (Icon && <Icon className="h-5 w-5" style={{ color: accentColor }} strokeWidth={2} />)}
          </div>
        )}
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            {badge != null && (
              <span
                className="flex h-5 w-5 items-center justify-center rounded-full text-[10px] font-bold text-white"
                style={{ backgroundColor: BRAND.navy }}
              >
                {badge}
              </span>
            )}
            <h2 id={titleId} className="text-base font-bold text-[#01114f]">
              {title}
            </h2>
            {titleExtra}
          </div>
          {subtitle && (
            <p className="mt-1 text-xs leading-relaxed text-[#99a1af]">{subtitle}</p>
          )}
        </div>
      </div>
      {right}
    </div>
  )
}

export function ReportSectionBody({
  children,
  className,
}: {
  children: ReactNode
  className?: string
}) {
  return <div className={cn('space-y-5 px-4 py-5 sm:px-6', className)}>{children}</div>
}
